/**
 * D-EZ-111 — THE BOOTH'S WRITING KIT.
 *
 * Small, pure helpers the broadcast, the storylines and the analyst share:
 *
 *   • `fact` — every name and number a written line uses is also written down
 *     as a Fact on the beat, so the grounding test can run the line through the
 *     same gate the model's lines go through (`verifyLine`).
 *   • `rotate` — which line out of a bank. A hash of stable ids (never a die),
 *     ROTATING with the league's race index, so the same driver does not hear
 *     the same line in consecutive weeks (measured: `npm run radio`).
 *   • words — ordinals, surnames, "last season", "here".
 */

import { stableHash } from '../story/hometown.ts';
import type { Fact, FactKind } from './researcher.ts';

/**
 * FNV-1a with a final avalanche (murmur3's fmix32). FNV alone leaves the low
 * bits of short, similar keys ("booth|host|season-101#1", "…-202#1") nearly
 * the same, and `% 8` reads exactly those bits — every league got the same
 * booth. Still a pure hash of stable ids, never a die.
 */
export function hash(text: string): number {
  let h = stableHash(text);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

export const fact = (kind: FactKind, text: string, value = 0, driverIds: string[] = []): Fact => ({ kind, text, value, driverIds });

/** One entry of a bank, and which one — the template id the variety harness counts. */
export interface Pick<T> {
  item: T;
  index: number;
}

/**
 * Pick from a bank. `seq` is the league's race index (it moves by one each
 * race), `occurrence` how many times this key has already spoken today. The
 * stride of two means a driver who hears this bank once or twice a race walks
 * through it two entries a week — an eight-line bank does not come back to the
 * same line for four races.
 */
export function rotate<T>(bank: readonly T[], key: string, seq: number, occurrence = 0): Pick<T> {
  const index = (hash(key) + seq * 2 + occurrence) % bank.length;
  return { item: bank[index], index };
}

export function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? name;
  return /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(last) && parts.length > 1 ? parts[parts.length - 2] : last;
}

export function ord(n: number): string {
  const v = n % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}

const ORD_WORDS = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];
/** "fourth" up to ten, "14th" after — how a booth says a position. */
export function ordWord(n: number): string {
  return n >= 1 && n <= 10 ? ORD_WORDS[n] : ord(n);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export const cap = (s: string): string => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));

/**
 * Fill `{slot}`s for a spoken line. `{x}'s` in a bank is always the contraction
 * ("Ackerd's out front"), and a name that ends in s says it out loud instead
 * ("Ricketts is out front", never "Ricketts's").
 */
export function fillSlots(template: string, slots: Record<string, string | number>): string {
  return template
    .replace(/\{(\w+)\}'s\b/g, (_, k: string) => {
      const v = String(slots[k] ?? '');
      return /s$/i.test(v) ? `${v} is` : `${v}'s`;
    })
    .replace(/\{(\w+)\}/g, (_, k: string) => String(slots[k] ?? ''));
}

/** Fill `{slot}`s. A missing slot is a bug in the caller, not something to paper over. */
export function fill(template: string, slots: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => {
    if (!(k in slots)) throw new Error(`booth template slot {${k}} has no value: ${template}`);
    return String(slots[k]);
  });
}

export interface Moment {
  seasonNumber: number;
  raceNumber: number;
}

/**
 * When something happened, from where the booth sits now — "last week",
 * "earlier this season", "last season", "back in season 2". Race 0 of a season
 * is the winter before it.
 */
export function whenPhrase(m: Moment, now: Moment): string {
  if (m.seasonNumber === now.seasonNumber) {
    if (m.raceNumber === 0) return 'over the winter';
    const d = now.raceNumber - m.raceNumber;
    if (d <= 1) return 'last week';
    if (d === 2) return 'two races ago';
    if (d <= 5) return 'a few races back';
    return 'earlier this season';
  }
  if (now.seasonNumber - m.seasonNumber === 1) return m.raceNumber === 0 ? 'over the winter' : 'last season';
  return `back in season ${m.seasonNumber}`;
}

/** The track as a person says it: era stamp gone, no shouting. Same rule as `setting.ts`. */
export function spokenTrack(name: string): string {
  const bare = name.replace(/^\s*(19|20)\d{2}s?\s+/, '').trim();
  if (!bare) return name;
  if (bare !== bare.toUpperCase()) return bare;
  return bare
    .toLowerCase()
    .split(' ')
    .map((w) => w.replace(/(^|-)([a-z])/g, (_, a: string, c: string) => a + c.toUpperCase()))
    .join(' ');
}

export const joinNames = (values: readonly string[]): string =>
  values.length < 2 ? values[0] ?? '' : `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
